/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import BaseApi from '../api/BaseApi';
import type {UserApi as Contract, IMS, IMSInfo, LogoutUrl} from '@adobe/exc-app/user';
import {Events} from '@exc/shared';
import type {RuntimeConfiguration} from '@adobe/exc-app';

export class UserApi extends BaseApi implements Contract {
  protected keyPrefix = 'user';
  protected apiEvents = [
    'avatar',
    'imsInfo',
    'imsOrg',
    'imsOrgName',
    'imsOrgs',
    'imsToken',
    'imsProfile',
    'locale',
    'preferredLanguages',
    'sandbox',
    'sandboxes',
    'subOrg',
    'tenant',
    'theme'
  ];

  public onConfigure(config: RuntimeConfiguration): void {
    const {changedProperties} = config;
    changedProperties
      .filter((changeKey: string) => this.apiEvents.includes(changeKey))
      .forEach((changeKey: string) => {
        if (changeKey !== 'imsInfo') {
          return this.emit(`change:${changeKey}`, (config as any)[changeKey]);
        }
        const changedImsInfo: IMSInfo = {};
        Object.keys((config as any)[changeKey]).forEach(key => {
          if ((changedProperties as any).includes(key)) {
            (changedImsInfo as any)[key] = (config as any)[key];
          }
        });
        this.emit(`change:${changeKey}`, (config as any)[changeKey], changedImsInfo);
      });
  }

  public authExpired(): void {
    this.metrics.event('Runtime.authExpired');
    this.messenger.send(Events.AUTH_EXPIRATION, {});
  }

  public generateSubOrg(productContext: Record<string, string>, overrides?: Record<string, string>): Promise<string> {
    this.metrics.event('Runtime.generateSubOrg', productContext, overrides);
    return this.sendAndReceive(Events.GENERATE_SUBORG, {data: {overrides, productContext}, method: 'GET', type: 'subOrg'});
  }

  public getCustomToken(ims: IMS): Promise<string> {
    this.metrics.event('Runtime.getCustomToken', ims);
    return this.sendAndReceive(Events.CUSTOM_TOKEN, {data: ims, method: 'GET', type: 'custom_token'});
  }

  public getFulfillableItems(serviceCode: string): Promise<string[]> {
    this.metrics.event('Runtime.getFulfillableItems', serviceCode);
    return this.sendAndReceive(Events.FULFILLABLE_ITEMS, {data: serviceCode, method: 'GET', type: 'fulfillableItems'});
  }

  public get logoutUrl(): LogoutUrl | undefined {
    return this.configManager.get('logoutUrl');
  }

  public set logoutUrl(value: LogoutUrl | undefined) {
    this.configManager.set({logoutUrl: value});
  }

  public get showLanguagePicker(): boolean | undefined {
    return this.configManager.get('showLanguagePicker');
  }

  public set showLanguagePicker(value: boolean | undefined) {
    this.configManager.set({showLanguagePicker: value});
  }

  public get showRolesPicker(): boolean | undefined {
    return this.configManager.get('showRolesPicker');
  }

  public set showRolesPicker(value: boolean | undefined) {
    this.configManager.set({showRolesPicker: value});
  }
}
